import { deepmerge } from "@mui/utils";
import { applyTokens, colorKeys } from "../../../utilities/applyTokens.js";
const focusedColors = (size, tokens) => {
    const toggle = tokens.component.toggle;
    const semantic = tokens.semantic;
    return {
        ".MuiButtonBase-root.MuiSwitch-switchBase": {
            "&:focus-within": {
                "+.MuiSwitch-track": {
                    backgroundColor: toggle.toggled.focus.bar.backgroundColor.value,
                    borderColor: toggle.toggled.focus.bar.backgroundColor.value,
                    boxShadow: semantic.shadow.focus.value.stringValue,
                },
                ".MuiSwitch-thumb": {
                    borderColor: toggle.toggled.focus.bar.backgroundColor.value,
                },
                "&:not(.Mui-checked)": {
                    "+ .MuiSwitch-track": {
                        borderColor: toggle.focus.bar.borderColor.value,
                        backgroundColor: toggle.bar.backgroundColor.value,
                    },
                    ".MuiSwitch-thumb": {
                        backgroundColor: toggle.thumb.backgroundColor.value,
                    },
                },
            },
        },
    };
};
// Default overrides that are used by both small and large sizes
// Replaces the wonky paddings and absolute positioning with glorious overlapping grid cells
const defaultOverrides = {
    display: "grid",
    width: "auto",
    height: "auto",
    overflow: "visible",
    alignItems: "center",
    padding: 0,
    "& .MuiButtonBase-root.MuiSwitch-switchBase": {
        position: "relative",
        padding: 0,
        gridRow: 1,
        gridColumn: 1,
        "& .MuiSwitch-thumb": {
            boxShadow: "none",
        },
        "& + .MuiSwitch-track": {
            gridRow: 1,
            gridColumn: 1,
        },
    },
};
// This modifies the MUI switch to look like a large Atlas switch, but doesn't add colors,
// other than the white fills that should always be used
const defaultsWithSize = (tokens, sizeTokens) => {
    const toggle = tokens.component.toggle;
    return {
        "& .MuiButtonBase-root.MuiSwitch-switchBase": {
            width: sizeTokens.thumb.width.value,
            height: sizeTokens.thumb.height.value,
            "& .MuiSwitch-thumb": {
                width: sizeTokens.thumb.width.value,
                height: sizeTokens.thumb.height.value,
            },
            "& + .MuiSwitch-track": Object.assign(Object.assign({}, applyTokens(toggle.bar, colorKeys)), { width: sizeTokens.bar.width.value, height: sizeTokens.bar.height.value, borderRadius: sizeTokens.bar.height.value, borderWidth: toggle.bar.borderWidth.value, borderStyle: "solid", opacity: 1 }),
            "&.Mui-checked": {
                transform: `translateX(calc(${sizeTokens.bar.width.value} - ${sizeTokens.thumb.width.value} - ${sizeTokens.paddingHorizontal.value}))`,
            },
            ":not(.Mui-checked)": {
                transform: ` translateX(${sizeTokens.paddingHorizontal.value})`,
            },
        },
    };
};
// Using tokens for primary
const createColorsFromTokens = (tokens) => {
    const toggle = tokens.component.toggle;
    return {
        "& .MuiButtonBase-root.MuiSwitch-switchBase": {
            "&.Mui-checked": {
                "& + .MuiSwitch-track": Object.assign({}, applyTokens(toggle.toggled.bar, colorKeys)),
                "& .MuiSwitch-thumb": Object.assign({}, applyTokens(toggle.thumb, colorKeys)),
                "&:hover": {
                    "& + .MuiSwitch-track": Object.assign({ opacity: 1 }, applyTokens(toggle.toggled.hover.bar, colorKeys)),
                },
                "&.Mui-disabled": {
                    "& + .MuiSwitch-track": Object.assign({}, applyTokens(toggle.toggled.disabled.bar, colorKeys)),
                    "& .MuiSwitch-thumb": Object.assign({}, applyTokens(toggle.thumb, colorKeys)),
                },
            },
            ":not(.Mui-checked)": {
                "& + .MuiSwitch-track": Object.assign({}, applyTokens(toggle.bar, colorKeys)),
                "& .MuiSwitch-thumb": Object.assign({}, applyTokens(toggle.thumb, colorKeys)),
                "&:hover": {
                    "& + .MuiSwitch-track": Object.assign({}, applyTokens(toggle.hover.bar, colorKeys)),
                    "& .MuiSwitch-thumb": Object.assign({}, applyTokens(toggle.thumb, colorKeys)),
                },
                "&.Mui-disabled": {
                    "& + .MuiSwitch-track": Object.assign({}, applyTokens(toggle.disabled.bar, colorKeys)),
                    "& .MuiSwitch-thumb": Object.assign({}, applyTokens(toggle.thumb, colorKeys)),
                },
            },
        },
    };
};
// This makes the custom switch design usable with any suppored Switch color
const createColorsFromMuiPalette = (palette, color) => {
    const paletteColor = color && color in palette ? palette[color] : palette.primary;
    if (!paletteColor) {
        return {};
    }
    return {
        "& .MuiButtonBase-root.MuiSwitch-switchBase": {
            "&:not(.Mui-checked)": {
                "& + .MuiSwitch-track": {
                    borderColor: paletteColor.main,
                },
                "& .MuiSwitch-thumb": {
                    backgroundColor: paletteColor.main,
                },
                "&:hover + .MuiSwitch-track": {
                    "& + .MuiSwitch-track": {
                        borderColor: paletteColor.dark,
                    },
                    "& .MuiSwitch-thumb": {
                        backgroundColor: paletteColor.dark,
                    },
                },
            },
            "&.Mui-checked": {
                "& + .MuiSwitch-track": {
                    borderColor: paletteColor.main,
                    backgroundColor: paletteColor.main,
                },
                "&:hover": {
                    "& + .MuiSwitch-track": {
                        backgroundColor: paletteColor.dark,
                        borderColor: paletteColor.dark,
                    },
                },
            },
        },
    };
};
const createStyles = ({ palette, currentSize, currentColor, tokens, targetSize, }) => {
    const switchSizeTokens = targetSize === "small" ? tokens.component.toggle.small : tokens.component.toggle.large;
    return (currentSize === targetSize &&
        deepmerge(defaultsWithSize(tokens, switchSizeTokens), Object.assign(Object.assign(Object.assign({}, (currentColor === "primary" && createColorsFromTokens(tokens))), (currentColor &&
            currentColor !== "primary" &&
            currentColor in palette &&
            createColorsFromMuiPalette(palette, currentColor))), focusedColors(targetSize, tokens))));
};
export const createToggleStyles = (overrides = {}) => {
    return deepmerge(defaultOverrides, Object.assign({}, overrides));
};
const switchTheme = (tokens) => {
    return {
        MuiSwitch: {
            styleOverrides: {
                root: ({ ownerState, theme }) => createToggleStyles(Object.assign(Object.assign({}, createStyles({
                    palette: theme.palette,
                    currentSize: ownerState === null || ownerState === void 0 ? void 0 : ownerState.size,
                    currentColor: ownerState === null || ownerState === void 0 ? void 0 : ownerState.color,
                    tokens,
                    targetSize: "small",
                })), createStyles({
                    palette: theme.palette,
                    currentSize: ownerState === null || ownerState === void 0 ? void 0 : ownerState.size,
                    currentColor: ownerState === null || ownerState === void 0 ? void 0 : ownerState.color,
                    tokens,
                    targetSize: "medium",
                }))),
            },
        },
    };
};
export default switchTheme;
//# sourceMappingURL=switch.js.map